import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Languages, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";
import { LANGS, SELL_COUNTRIES, type Lang, type SellCountry } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/locale-context";
import { cn } from "@/lib/utils";
import { CountryFlagIcon } from "@/components/CountryFlagIcon";

const LANGUAGE_FLAG: Record<Lang, string> = { sv: "SE", en: "GB", es: "ES" };
const LANGUAGE_LABEL: Record<Lang, string> = {
  sv: "Svenska",
  en: "English",
  es: "Español",
};
const COUNTRY_LABEL: Record<Lang, Record<SellCountry, string>> = {
  sv: { SE: "Sverige", ES: "Spanien" },
  en: { SE: "Sweden", ES: "Spain" },
  es: { SE: "Suecia", ES: "España" },
};
export function LocalePicker({ className, expanded = false }: { className?: string; expanded?: boolean }) {
  const { lang, country, setLanguage, setCountry } = useLocale();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);
  const chooseLanguage = (next: Lang) => {
    setOpen(false);
    if (next === lang) return;
    setLanguage(next);
  };
  const chooseCountry = (next: SellCountry) => {
    setOpen(false);
    if (next === country) return;
    setCountry(next);
  };

  return (
    <div ref={pickerRef} className={cn("relative", expanded && "w-full")}>
        <Button
          type="button"
          variant="ghost"
          size={expanded ? "sm" : "icon"}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${t("common.language")}: ${LANGUAGE_LABEL[lang]}. ${t("common.sellCountry")}: ${COUNTRY_LABEL[lang][country]}`}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            "h-9 rounded-full border border-border bg-background px-1.5 shadow-sm hover:bg-accent",
            expanded && "w-full justify-between px-3",
            className,
          )}
        >
          <span className="flex min-w-0 items-center">
            <CountryFlagIcon countryCode={LANGUAGE_FLAG[lang]} size="xs" className="ring-1 ring-background" />
            <CountryFlagIcon countryCode={country} size="xs" className="-ml-1 ring-1 ring-background" />
            {expanded && <span className="ml-2 truncate text-xs font-semibold">{LANGUAGE_LABEL[lang]} · {COUNTRY_LABEL[lang][country]}</span>}
          </span>
          <ChevronDown className={cn("h-3 w-3 shrink-0 text-muted-foreground", expanded ? "ml-2" : "ml-0.5")} />
        </Button>
      {open && <div
        role="dialog"
        aria-label={`${t("common.language")} · ${t("common.sellCountry")}`}
        className="absolute right-0 top-full z-[100] mt-2 w-[calc(100vw-1.5rem)] max-w-xs rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-lg sm:w-72"
      >
        <div className="flex items-center gap-1.5 px-2 pb-1.5 pt-1 text-xs font-semibold text-muted-foreground">
          <Languages className="h-3.5 w-3.5" /> {t("common.language")}
        </div>
        <div className="space-y-1">
          {LANGS.map((item) => (
            <Button
              key={item}
              type="button"
              variant="ghost"
              onClick={() => chooseLanguage(item)}
              className={cn(
                "grid h-10 w-full grid-cols-[2rem_minmax(0,1fr)_1.25rem] items-center gap-2 rounded-md px-2.5 text-sm font-medium text-popover-foreground hover:bg-accent hover:text-accent-foreground",
                item === lang && "bg-primary/10 text-primary focus:bg-primary/10 focus:text-primary",
              )}
            >
              <CountryFlagIcon countryCode={LANGUAGE_FLAG[item]} size="sm" className="justify-self-start" />
              <span className="min-w-0 truncate text-left">{LANGUAGE_LABEL[item]}</span>
              {item === lang ? <Check className="h-4 w-4 shrink-0 justify-self-end" /> : <span aria-hidden="true" />}
            </Button>
          ))}
        </div>
        <div className="-mx-1 my-2 h-px bg-border" />
        <div className="px-2 pb-1.5 pt-0.5">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {t("common.sellCountry")}</span>
          <span className="mt-0.5 block text-[11px] font-normal leading-snug text-muted-foreground">{t("common.sellCountryHint")}</span>
        </div>
        <div className="space-y-1">
          {SELL_COUNTRIES.map((item) => (
            <Button
              key={item}
              type="button"
              variant="ghost"
              onClick={() => chooseCountry(item)}
              className={cn(
                "grid h-10 w-full grid-cols-[2rem_minmax(0,1fr)_1.25rem] items-center gap-2 rounded-md px-2.5 text-sm font-medium text-popover-foreground hover:bg-accent hover:text-accent-foreground",
                item === country && "bg-primary/10 text-primary focus:bg-primary/10 focus:text-primary",
              )}
            >
              <CountryFlagIcon countryCode={item} size="sm" className="justify-self-start" />
              <span className="min-w-0 truncate text-left">{COUNTRY_LABEL[lang][item]}</span>
              {item === country ? <Check className="h-4 w-4 shrink-0 justify-self-end" /> : <span aria-hidden="true" />}
            </Button>
          ))}
        </div>
      </div>}
    </div>
  );
}
